import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const nav = useNavigate();

  // 🔐 Verify invite token
  useEffect(() => {
    if (!token) return;

    api
      .get(`/admin/admin-users/invite/verify?token=${token}`)
      .then(() => {
        setValid(true);
      })
      .catch(() => {
        toast.error("Invalid or expired invite");
      })
      .finally(() => setLoading(false));
  }, [token]);

  // 🚀 Submit password
  const submit = async (e) => {
    e.preventDefault();

    if (!password) {
      return toast.error("Password is required");
    }

    if (password.length < 8) {
      return toast.error("Minimum 8 characters required");
    }

    if (password !== confirm) {
      return toast.error("Passwords do not match");
    }

    try {
      await api.post("/admin/admin-users/invite/set-password", {
        token,
        password,
      });

      toast.success("Password set successfully");

      setTimeout(() => {
        nav("/login");
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to set password");
    }
  };

  // ⏳ Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Validating invite...
      </div>
    );
  }

  // ❌ Invalid token
  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Invalid or expired invite link
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <form
        onSubmit={submit}
        className="bg-white border rounded-2xl p-6 w-full max-w-sm mx-4 shadow-sm space-y-4"
      >
        <h2 className="text-xl font-semibold text-gray-800">
          Create Password
        </h2>

        {/* Password */}
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            className="w-full border rounded-lg px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black bg-gray-100"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <input
            type={showConfirm ? "text" : "password"}
            placeholder="Confirm Password"
            className={`w-full border rounded-lg px-3 py-2 pr-10 outline-none focus:ring-2 ${
              confirm && password !== confirm
                ? "border-red-400 focus:ring-red-400"
                : "focus:ring-black bg-gray-100"
            }`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
          >
            {showConfirm ? "🙈" : "👁️"}
          </button>
        </div>

        {/* Inline error */}
        {confirm && password !== confirm && (
          <p className="text-xs text-red-500">Passwords do not match</p>
        )}

        {/* Password hint */}
        {password && password.length < 8 && (
          <p className="text-xs text-gray-500">
            Minimum 8 characters required
          </p>
        )}

        {/* Submit */}
        <button
          disabled={
            !password ||
            password.length < 8 ||
            password !== confirm
          }
          className="w-full bg-black text-white py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
        >
          Set Password
        </button>
      </form>
    </div>
  );
}